import { round2 } from '@shared/utils/money';

/**
 * Per-vendor deposit allocation — the maths behind "how much of THIS payment goes to EACH
 * vendor on the booking".
 *
 * A booking with more than one vendor collects the customer's money in phases, and the reseller
 * decides how each phase is divided. Two input modes:
 *
 *   auto   — one figure drives the phase; each vendor takes a slice PRO-RATA BY COST SHARE.
 *   manual — the reseller types a RM figure per vendor; untouched rows stay auto and re-absorb
 *            whatever is left.
 *
 * Two rules this file exists to keep straight, both of which were explicitly chosen over the
 * obvious alternatives:
 *
 *   • Auto splits by COST SHARE, not equally. Splitting a phase equally in RM hands a RM 3,000
 *     photographer the same as a RM 10,000 hall.
 *   • Auto is a percentage OF THE PHASE, not of each vendor's own total. Those are identical
 *     when partner costs sum to the order total and diverge badly when they don't (a real
 *     package exists with a customer price of RM 10,000 against RM 115,000 of partner cost).
 *     Only "% of the phase" can never over-allocate money that was actually collected.
 *
 * Everything here is pure so it can be tested without the broken repo-wide `ng test`.
 */

/** One vendor's row inside one phase's allocation grid. */
export interface AllocRow {
  /** packageItemId (offering template) or bookingPackageId (booking) — identity within the grid. */
  key: string;
  vendorId: number;
  /** service name */
  name: string;
  vendorName: string;
  iconCode: string;
  color: string;
  /** 0..1 — this vendor's share of TOTAL PARTNER COST (never of the customer price). */
  costShare: number;
  /** their whole slice of the booking, for the across-phases over-allocation check. */
  vendorTotal: number;
  /** allocated out of this phase */
  amount: number;
  /** false once the reseller has typed this cell; that row stops re-deriving. */
  isAuto: boolean;
}

/**
 * Re-derive every AUTO row.
 *
 * A vendor is paid what their SERVICE COSTS, spread evenly across the phases — not a slice of
 * every phase. Formally, each auto row targets
 *
 *     cost × min(1, packageTotal / totalCost) × (phaseAmount / packageTotal)
 *
 * so across a full schedule (Σ phases = packageTotal) each vendor receives exactly their cost and
 * the reseller keeps the margin, phase by phase.
 *
 * This replaces a pro-rata split of the whole phase, which quietly handed the reseller's margin
 * to the vendors: on a RM 30,000 package holding RM 29,100 of services, a hall costing RM 25,000
 * collected RM 25,773.14 — 86% of the package price rather than its own price — because 86% of
 * the cost meant 86% of the margin too. `you keep` read RM 0.00 on every phase.
 *
 * `min(1, packageTotal / totalCost)` is what happens when the services cost MORE than the package
 * sells for (a real Full Package exists at RM 10,000 against RM 115,000 of partner cost). Paying
 * each vendor their full cost is then impossible, so everyone is scaled back to fit what the
 * customer actually pays — which is exactly the old pro-rata split. The two rules agree wherever
 * there is no margin to misplace, so a break-even package behaves as it always did.
 *
 * Manual rows are left alone and consume their amount off the top; auto rows fit inside what is
 * left. The last auto row takes the rounding remainder (largest-remainder, the technique the
 * wallet's held-split uses) so the column lands on its target to the sen.
 *
 * `packageTotal` omitted or non-positive keeps the legacy behaviour — the whole phase is shared
 * out — because without it there is no way to know what a vendor's cost is a fraction OF.
 */
export function autoAllocate(rows: AllocRow[], phaseAmount: number, packageTotal = 0): AllocRow[] {
  const manual = rows.reduce((s, r) => s + (r.isAuto ? 0 : (r.amount || 0)), 0);
  const pool = round2(Math.max(0, phaseAmount - manual));
  const autoIdx = rows.map((r, i) => (r.isAuto ? i : -1)).filter(i => i >= 0);
  if (!autoIdx.length) return rows.map(r => ({ ...r }));

  const totalCost = rows.reduce((s, r) => s + Math.max(0, r.vendorTotal || 0), 0);
  const out = rows.map(r => ({ ...r }));

  // What each auto row is aiming for, before it has to fit in `pool`.
  let targets: number[];
  if (packageTotal > 0 && totalCost > 0) {
    const factor = Math.min(1, packageTotal / totalCost);
    const progress = phaseAmount / packageTotal;
    targets = autoIdx.map(i => Math.max(0, rows[i].vendorTotal || 0) * factor * progress);
  } else {
    // No package price to measure against, or nothing priced yet: fall back to sharing the pool
    // by cost share, evenly when no row carries any cost at all.
    const shareSum = autoIdx.reduce((s, i) => s + Math.max(0, rows[i].costShare), 0);
    targets = autoIdx.map(i =>
      pool * (shareSum > 0 ? Math.max(0, rows[i].costShare) / shareSum : 1 / autoIdx.length));
  }

  // Never promise more than this phase collects. Manual rows may already have spent it.
  const wanted = targets.reduce((s, v) => s + v, 0);
  if (wanted > pool && wanted > 0) {
    const squeeze = pool / wanted;
    targets = targets.map(v => v * squeeze);
  }
  const goal = round2(Math.min(wanted, pool));

  let distributed = 0;
  autoIdx.forEach((rowIx, n) => {
    if (n === autoIdx.length - 1) {
      out[rowIx].amount = round2(Math.max(0, goal - distributed));
      return;
    }
    const part = round2(Math.max(0, targets[n]));
    out[rowIx].amount = part;
    distributed = round2(distributed + part);
  });
  return out;
}

/** What the reseller keeps out of this phase — negative means they allocated more than it collects. */
export function unallocated(rows: AllocRow[], phaseAmount: number): number {
  return round2(phaseAmount - rows.reduce((s, r) => s + (r.amount || 0), 0));
}

/**
 * Hold a phase's typed figure inside what it could possibly collect.
 *
 * A percentage above 100 is not a schedule, it is a typo: "150% of total" asks the customer for
 * half again the package price. The same is true of a ringgit figure larger than the order — so
 * `fixed` is capped at `total` when the caller knows it.
 *
 * Returns the string UNTOUCHED whenever it is already in range, so a half-typed "0." or an empty
 * box survives editing. Rewriting every keystroke through parseFloat would make "0.5" impossible
 * to type: the "0." step would round-trip as "0".
 */
export function clampPhaseValue(mode: string, raw: string, total = 0): string {
  const n = parseFloat(raw);
  if (isNaN(n)) return raw;
  if (n < 0) return '0';
  // Both percentage modes are bounded at 100: "% of total" is an increment, "Pay up to %" is a
  // cumulative target, and neither can sensibly ask for more than the whole order.
  if (mode === 'pct' || mode === 'upto') return n > 100 ? '100' : raw;
  // A ringgit figure is bounded by the order itself. `schedAmount` already clamps what a row
  // COLLECTS to what the rows above it left, but without this the box still displayed a figure
  // larger than the whole booking while quietly collecting less — which reads as the schedule
  // ignoring what was typed. `total <= 0` means nothing is priced yet, so leave it alone.
  if (mode === 'fixed' && total > 0) return n > total ? String(round2(total)) : raw;
  return raw;
}

// ─────────────────────────────────────────────────────────────────────────────
// The assumed tier — a PRIVACY device, not an estimate
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The early-release rate every allocation surface pretends each vendor is on.
 *
 * A vendor's real advance tier is private to them. It is also trivially inferable from any
 * REAL figure derived from it: show a reseller that a partner may draw RM 700 against a
 * RM 7,000 slice and they have just learned that partner is on a 10% tier — no different from
 * printing the tier name outright.
 *
 * So every surface outside the owning vendor's own wallet quotes this flat assumption instead.
 * Because it is identical for every vendor, it reveals nothing about any of them: it is a
 * placeholder occupying the slot where a revealing number would otherwise go.
 *
 * It is deliberately NOT used in `releaseGate`. What a vendor may actually draw is computed
 * from their real tier, in their own wallet, where they are the only viewer. This constant
 * never touches money that moves — it exists purely so allocation screens can say something
 * useful without saying something private.
 */
export const ASSUMED_TIER_PCT = 50;

/**
 * The early-release ceiling to SHOW for a vendor on an allocation screen, at the assumed tier.
 *
 * Display only. Their real ceiling is `releaseGate(...)`, visible to them alone.
 */
export function assumedEarlyCeiling(vendorTotal: number): number {
  return round2(Math.max(0, vendorTotal) * ASSUMED_TIER_PCT / 100);
}

/**
 * Cost shares from what each vendor COSTS, not what the customer pays. Costs must already be
 * resolved through the supplier's per-reseller ResaleTerm (`termsRec` / `resalePriceFor`) —
 * `Offering.basePrice` is the catalogue price, not what this reseller owes.
 */
export function costShares(costs: number[]): number[] {
  const total = costs.reduce((s, c) => s + Math.max(0, c), 0);
  if (total <= 0) return costs.map(() => (costs.length ? 1 / costs.length : 0));
  return costs.map(c => Math.max(0, c) / total);
}

/**
 * Cost shares as whole percents that actually ADD UP TO 100.
 *
 * Rounding each share on its own does not: 0.36 / 18.12 / 9.06 / 72.46 rounds to 0 / 18 / 9 / 72,
 * a visible 99% against a column of ringgit that sums exactly. Largest-remainder hands the lost
 * points to the shares with the biggest fractions — the same technique `autoAllocate` uses to
 * make the money column land on the phase exactly.
 *
 * A share too small to round up to 1 keeps a floor of `0`; the caller labels those `<1%` rather
 * than `0%`, because "0%" beside a real allocation of RM 32.61 reads as a bug.
 */
export function sharePercents(shares: number[]): number[] {
  const raw = shares.map(s => Math.max(0, s) * 100);
  const out = raw.map(v => Math.floor(v));
  let left = 100 - out.reduce((s, v) => s + v, 0);
  if (left <= 0) return out;
  // Biggest fractional part first; ties fall to the earlier row so the order is deterministic.
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => (b.frac - a.frac) || (a.i - b.i));
  for (const { i } of order) {
    if (left <= 0) break;
    out[i] += 1;
    left--;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Staleness — re-scale first, block only when re-scaling cannot save it
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Re-scale a phase's allocations from one phase amount to another, keeping every proportion.
 *
 * This is the SILENT half of staleness: a price change (base price, extra head, a partner
 * re-pricing their service) still leaves the split meaningful and the column still sums, so
 * re-scale and say nothing. Blocking on every price tweak would be noise. Manual rows scale
 * too — the reseller expressed a proportion, not a promise of an exact ringgit figure — but
 * they stay manual so a later auto pass doesn't overwrite them.
 */
export function rescaleAllocations(
  rows: AllocRow[], fromPhase: number, toPhase: number, packageTotal = 0,
): AllocRow[] {
  if (fromPhase <= 0) {
    return autoAllocate(rows.map(r => ({ ...r, isAuto: true })), toPhase, packageTotal);
  }
  const factor = toPhase / fromPhase;
  const scaled = rows.map(r => ({ ...r, amount: round2((r.amount || 0) * factor) }));
  // Re-run the auto pass so the rounding remainder lands on a row instead of leaving the
  // column a sen off its target.
  return autoAllocate(scaled, toPhase, packageTotal);
}

/**
 * Bring an existing phase grid in step with a re-costed order whose PARTIES are unchanged.
 *
 * Extra pax or an add-on on a Full Package moves the pieces' costs without adding a row, so the
 * grid is not rebuilt — but its `vendorTotal`/`costShare` must still follow, or Auto keeps paying
 * every vendor their pre-edit cost and the increase lands in "you keep" (QA 2026-10-19: +RM 200
 * on a Full Package read "you keep RM 60.00" on a 30% deposit). `live` is the freshly-costed grid
 * in the same row order as `prev`; each row keeps its typed amount and auto/manual flag.
 *
 * Returns `prev` itself when nothing moved, so an unchanged grid keeps its identity.
 */
export function reconcileAllocPhase(
  prev: AllocRow[], live: AllocRow[], fromPhase: number, toPhase: number, packageTotal = 0,
): AllocRow[] {
  const merged = prev.map((a, k) => ({ ...live[k], amount: a.amount, isAuto: a.isAuto }));
  const out = fromPhase !== toPhase
    ? rescaleAllocations(merged, fromPhase, toPhase, packageTotal)
    : autoAllocate(merged, toPhase, packageTotal);
  const same = out.every((a, k) => a.amount === prev[k].amount
    && a.vendorTotal === prev[k].vendorTotal && a.costShare === prev[k].costShare);
  return same ? prev : out;
}

/** One phase, as the invariant checks see it. */
export interface AllocPhase { label: string; amount: number; rows: AllocRow[]; }

/**
 * Every way an allocation is broken, in the order the editor shows them.
 *
 * These are the invariants that survive a re-scale: a phase that over-allocates, or a vendor
 * handed more across all phases than their whole slice is worth. A tier change is NOT here —
 * allocation is tier-independent, so a vendor moving between advance tiers invalidates nothing.
 */
export function allocationWarnings(phases: AllocPhase[]): string[] {
  const out: string[] = [];
  const rm = (n: number) => 'RM ' + n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  for (const p of phases) {
    const left = unallocated(p.rows, p.amount);
    if (left < -0.004) {
      out.push(`“${p.label || 'This payment'}” allocates ${rm(-left)} more than it collects.`);
    }
  }

  // Across phases: nobody may be handed more than their own slice of the booking is worth.
  //
  // Two different sums, and conflating them is why this needs care. `allocated` adds up EVERY
  // (phase, row) pair — that is the point of the check. `total` must count each SERVICE once,
  // however many phases pay it, so it is keyed on `r.key` rather than re-added per phase.
  //
  // `Math.max` used to stand in for that. It is safe for a vendor with one service and wrong for
  // a vendor with several: a hall supplying a venue, a makeup package and a second hall was
  // credited only with its single largest line, so once the phases allocated its real share the
  // check fired a warning against money it was genuinely owed.
  const byVendor = new Map<number, { name: string; allocated: number; total: number; seen: Set<string> }>();
  for (const p of phases) {
    for (const r of p.rows) {
      const cur = byVendor.get(r.vendorId)
        ?? { name: r.vendorName || r.name, allocated: 0, total: 0, seen: new Set<string>() };
      cur.allocated = round2(cur.allocated + (r.amount || 0));
      if (!cur.seen.has(r.key)) {
        cur.seen.add(r.key);
        cur.total = round2(cur.total + (r.vendorTotal || 0));
      }
      byVendor.set(r.vendorId, cur);
    }
  }
  for (const v of byVendor.values()) {
    if (v.allocated - v.total > 0.004) {
      // The check stays — over-allocating a vendor is still broken — but the message must not
      // quote what they are OWED. That figure is the vendor's own economics, and printing it
      // beside their name turns a validation warning into a disclosure surface.
      out.push(`${v.name} is allocated ${rm(v.allocated)} across all payments — more than their share of this booking is worth.`);
    }
  }
  return out;
}

/**
 * Does re-scaling save this package, or must it be blocked?
 *
 * Structural change (a bundled vendor removed or swapped) can't be re-scaled — there is no row
 * to scale — and neither can a re-scale that breaks an invariant. Either way the package stops
 * being bookable and the vendor is told, through the same `staleReasons` → `.alertbox` path
 * /vendor/services already uses, so all four buyer surfaces hide it consistently.
 */
export function allocationStaleReasons(
  phases: AllocPhase[],
  /** vendor ids still on the package — a share pointing outside this set lost its service. */
  liveVendorIds: Set<number>,
): string[] {
  const reasons: string[] = [];
  const gone = new Set<string>();
  for (const p of phases) {
    for (const r of p.rows) {
      if (!liveVendorIds.has(r.vendorId) && !gone.has(r.vendorName || r.name)) {
        gone.add(r.vendorName || r.name);
        reasons.push(`${r.vendorName || r.name} is no longer part of this package — the deposit split still sends money to them.`);
      }
    }
  }
  return [...reasons, ...allocationWarnings(phases)];
}

// ─────────────────────────────────────────────────────────────────────────────
// The release gate (wallet)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How much of each phase the customer has actually paid, given the cumulative amount received.
 * Same covered/partial logic as the wallet's schedule rows: a phase is funded only once the
 * running total reaches it, and a partly-funded phase counts only the part that arrived.
 */
export function phaseCoverage(phaseAmounts: number[], netPaid: number): number[] {
  let prior = 0;
  return phaseAmounts.map(a => {
    const covered = Math.max(0, Math.min(a, netPaid - prior));
    prior += a;
    return round2(covered);
  });
}

/**
 * What a vendor has been allocated out of money the customer HAS PAID.
 *
 * A phase contributes nothing until it is collected — there is no releasing against money that
 * hasn't arrived — and a partly-collected phase contributes its covered fraction only.
 */
export function allocatedCollected(
  shares: number[], phaseAmounts: number[], netPaid: number,
): number {
  const covered = phaseCoverage(phaseAmounts, netPaid);
  let sum = 0;
  phaseAmounts.forEach((amt, i) => {
    if (amt <= 0) return;
    sum += (shares[i] ?? 0) * (covered[i] / amt);
  });
  return round2(sum);
}

export interface ReleaseGate {
  /** What this vendor may draw right now. */
  releasable: number;
  /** Drawn beyond the current ceiling — the clawback the wallet had no DB source for. */
  clawback: number;
  /** The binding ceiling, for the note under the figure. */
  cap: number;
}

/**
 * The release ceiling: BOTH limits apply, and the result floors at zero.
 *
 *     releasable = max(0, min(allocated across collected phases,
 *                             tierPct x their total slice) - already drawn)
 *
 * `max(0, …)` is not defensive noise: a tier can DROP after a draw (a completed booking
 * reversed, or an admin editing AdvanceTier.MaxAdvancePercent), which would otherwise take the
 * figure negative. What was drawn above the new ceiling becomes clawback rather than a negative
 * "releasable".
 */
export function releaseGate(
  allocatedForCollectedPhases: number, tierPct: number, vendorTotal: number, alreadyDrawn: number,
): ReleaseGate {
  const tierCap = round2(Math.max(0, vendorTotal) * Math.max(0, tierPct) / 100);
  const cap = round2(Math.min(Math.max(0, allocatedForCollectedPhases), tierCap));
  return {
    cap,
    releasable: round2(Math.max(0, cap - Math.max(0, alreadyDrawn))),
    clawback: round2(Math.max(0, Math.max(0, alreadyDrawn) - cap)),
  };
}

/**
 * Does one phase's split fail to balance?
 *
 * The mirror of what the grid's footer shows, so the editors can BLOCK a save on exactly the
 * condition the vendor is being warned about — a warning that still lets the work be saved is
 * just a suggestion, and this one guards money: an over-allocated phase promises vendors more
 * than the customer pays into it, and an under-allocated one leaves part of a payment with no
 * recipient at all.
 *
 * Auto always balances by construction, so only a typed grid can trip the under-allocated half.
 */
export function isPhaseUnbalanced(rows: AllocRow[], phaseAmount: number): boolean {
  if (!rows.length) return false;
  const keep = unallocated(rows, phaseAmount);
  if (keep < -0.004) return true;                       // over-allocated, in either mode
  return rows.some(r => !r.isAuto) && keep > 0.004;     // under-allocated, manual only
}

/** One bundled service's standing across the WHOLE schedule. */
export interface AllocServiceTotal {
  key: string;
  name: string;
  vendorName: string;
  /** Handed to this service across every phase. */
  allocated: number;
  /** What the service is worth — its partner cost (own pieces: what the tier price leaves). */
  owed: number;
  /** `owed − allocated`, floored at 0: still to be assigned somewhere in the schedule. */
  unassigned: number;
  /** `allocated − owed`, floored at 0: handed more across the schedule than the service is worth. */
  over: number;
}

/**
 * What every bundled service has been allocated across all phases, against what it is owed.
 *
 * `allocationWarnings` already aggregates this to decide whether a vendor was handed more than
 * their slice is worth, but it can only ever return sentences. The editor needs the same figures to
 * SHOW — a vendor filling in a schedule wants to see that the caterer is RM 2,000 short before the
 * save button tells them, and per SERVICE rather than per vendor, because one vendor can supply
 * several pieces of the same package and a single merged line hides which one is unfinished.
 *
 * `allocated` adds up every (phase, row) pair — that is the point. `owed` counts each service ONCE
 * however many phases pay it, so it is keyed on `row.key`.
 */
export function allocatedByService(phases: AllocPhase[]): AllocServiceTotal[] {
  const by = new Map<string, AllocServiceTotal>();
  for (const p of phases) {
    for (const r of p.rows) {
      const cur = by.get(r.key)
        ?? {
          key: r.key, name: r.name, vendorName: r.vendorName,
          allocated: 0, owed: round2(r.vendorTotal || 0), unassigned: 0, over: 0,
        };
      cur.allocated = round2(cur.allocated + (r.amount || 0));
      by.set(r.key, cur);
    }
  }
  // Both directions are worth showing. A service left short is the obvious one, but a phase split
  // by percent rounds, and a few sen handed out beyond what a service is owed accumulates across
  // phases into money the schedule promises twice.
  return [...by.values()].map(v => ({
    ...v,
    unassigned: round2(Math.max(0, v.owed - v.allocated)),
    over: round2(Math.max(0, v.allocated - v.owed)),
  }));
}

/**
 * Why this Full Package schedule's PER-SERVICE split can't be saved, or null when it can.
 *
 * `isPhaseUnbalanced` only checks each payment against itself, so a deposit of RM 10,000 typed
 * entirely onto a RM 3,000 photographer balanced and saved — leaving the caterer and the hall with
 * nothing and the photographer RM 7,000 over (QA 2026-10-15). Every later figure that reads the
 * allocation (booking phases, escrow release, the wallet's vendor breakdown) inherits that.
 *
 *  - OVER: no service may be handed more across the schedule than it is owed.
 *  - LEFT: once the payments collect the whole price — and the services' total fits inside it —
 *    every service must be fully assigned. A schedule still short of the price isn't judged here
 *    (`milestoneTotals` already shows that gap), nor one whose services cost more than it collects.
 *
 * Manual grids only: auto fills every row from its cost by construction, and the sen its per-phase
 * rounding leaves must not block a save the vendor never typed (same rule as `isPhaseUnbalanced`).
 */
export function serviceAllocationProblem(phases: AllocPhase[], price: number): string | null {
  if (!phases.some(p => p.rows.some(r => !r.isAuto))) return null;
  const rm = (n: number) => 'RM ' + n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const services = allocatedByService(phases);
  const over = services.find(s => s.over > 0.004);
  if (over) return `${over.name} is allocated ${rm(over.over)} more than its share — move it to another service.`;
  const collected = round2(phases.reduce((s, p) => s + (p.amount || 0), 0));
  const owedTotal = round2(services.reduce((s, v) => s + v.owed, 0));
  if (price > 0 && collected >= price - 0.004 && owedTotal <= collected + 0.004) {
    const left = services.find(s => s.unassigned > 0.004);
    if (left) return `${left.name} still has ${rm(left.unassigned)} left to allocate.`;
  }
  return null;
}
